import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAllInvites } from "../api/invites";
import {
  getAcceptedInvites,
  getReceivedPendingInvites,
  getSentPendingInvites,
} from "../helpers/utils";
import { useCallback, useMemo } from "react";
import useUsers from "./useUsers";
import networkStore from "src/store/networkStore";
import useExtraModules from "./useExtraModules";
import { APP_USER_ID_KEY } from "src/constants/app";
import { CACHE_KEYS } from "src/constants/cache";

const REFETCH_MINUTES = 10;
const REFETCH_INTERVAL = REFETCH_MINUTES * 1000 * 60;

export default function useInvites() {
  const queryClient = useQueryClient();
  const { myNetworkIsEnabled } = useExtraModules();
  const currentUserId = localStorage?.getItem?.(APP_USER_ID_KEY) || "";
  const { isLoadingUsers } = useUsers();
  const { acceptInvite, deleteInvite, searchNetwork, sendInvite } =
    networkStore();

  const {
    data: invites,
    isLoading,
    isRefetching,
  } = useQuery({
    queryFn: async () => await getAllInvites(),
    queryKey: [CACHE_KEYS.ALL_INVITES],
    refetchInterval: REFETCH_INTERVAL,
  });

  const resetCache = async () => {
    return await queryClient.refetchQueries({
      queryKey: [CACHE_KEYS.ALL_INVITES],
    });
  };

  const acceptedInvites = useMemo(() => {
    return getAcceptedInvites({
      invites: invites?.data?.data || [],
      user: currentUserId,
    });
  }, [isLoading, invites?.data?.data]);

  const receivedInvites = useMemo(() => {
    if (!myNetworkIsEnabled) return [];

    return getReceivedPendingInvites({
      invites: invites?.data?.data || [],
      user: currentUserId,
    });
  }, [isLoading, invites?.data?.data, myNetworkIsEnabled]);

  const sentInvites = useMemo(() => {
    return getSentPendingInvites({
      invites: invites?.data?.data || [],
      user: currentUserId,
    });
  }, [isLoading, invites?.data?.data]);

  const acceptContactInvite = useCallback(async (inviteId: string) => {
    return await acceptInvite({ inviteIds: [inviteId] });
  }, []);

  const deleteContactInvite = useCallback(async (inviteId: string) => {
    return await deleteInvite({ inviteIds: [inviteId] });
  }, []);

  const sendContactInvite = useCallback(
    async ({
      sentToUserId,
      sentToEmail,
      sentByName,
      sentToName,
    }: {
      sentToUserId: string;
      sentToEmail: string;
      sentByName: string;
      sentToName: string;
    }) => {
      return await sendInvite({
        to: sentToUserId,
        sendToThisEmail: sentToEmail,
        sentByName,
        sentToName,
      });
    },
    [],
  );

  const searchPeople = useCallback(async (searchTerm: string) => {
    return await searchNetwork(searchTerm);
  }, []);

  return {
    allInviteList: invites?.data || [],
    isLoadingInvites: !!isLoading || isLoadingUsers,
    isRefetchingInvites: !!isRefetching,
    resetInvitesCache: resetCache,
    acceptedInvites,
    receivedInvites,
    acceptContactInvite,
    deleteContactInvite,
    searchPeople,
    sentInvites,
    sendContactInvite,
  };
}
